import React, { useCallback, useEffect, useState } from 'react';
import { helpWhy } from '../../help/why';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { PayrollProvisionService } from '../../services/payrollProvision.service';
import {
    IFestivalLine, IFestivalPlan, IFestivalPreview, IFestivalRun, IFestivalRunDetail,
} from '../../interfaces/payrollProvision.interface';
import { useToast } from '../../context/ToastContext';
import {
    ConfirmDialog, JournalLink, PeriodPicker, PreviewNotices, RunStatusChip, SummaryStats, currentPeriod, money,
    periodLabel, useSegmentNames,
} from './provisionShared';

interface Props {
    institutionId: string;
    canManage: boolean;
}

// W3-J: festival-bonus provision. fiscalYear is the calendar year in which the institution's fiscal year STARTS.
const FestivalProvisionTab: React.FC<Props> = ({ institutionId, canManage }) => {
    const { showSuccess, showApiError } = useToast();
    const segmentName = useSegmentNames(institutionId);
    const [plans, setPlans] = useState<IFestivalPlan[]>([]);
    const [runs, setRuns] = useState<IFestivalRun[]>([]);
    const [loading, setLoading] = useState(false);
    const [period, setPeriod] = useState(currentPeriod);
    const [preview, setPreview] = useState<IFestivalPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [posting, setPosting] = useState(false);
    const [detail, setDetail] = useState<IFestivalRunDetail | null>(null);
    const [reverseTarget, setReverseTarget] = useState<IFestivalRun | null>(null);
    const [reversing, setReversing] = useState(false);
    // plan dialog: null = closed; {id: undefined} = new
    const [planDialog, setPlanDialog] = useState<{ id?: string } | null>(null);
    const [planYear, setPlanYear] = useState('');
    const [planPercent, setPlanPercent] = useState('');
    const [planNote, setPlanNote] = useState('');
    const [savingPlan, setSavingPlan] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<IFestivalPlan | null>(null);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [p, r] = await Promise.all([
                PayrollProvisionService.festivalPlans(institutionId),
                PayrollProvisionService.festivalRuns(institutionId),
            ]);
            setPlans(p);
            setRuns(r);
        } catch (e) {
            showApiError(e, 'Failed to load festival bonus provisions');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const openPlan = (plan?: IFestivalPlan) => {
        setPlanDialog({ id: plan?.id });
        setPlanYear(plan ? String(plan.fiscalYear) : String(new Date().getFullYear()));
        setPlanPercent(plan ? String(plan.totalPercentOfBasic) : '');
        setPlanNote(plan?.note ?? '');
    };

    const planValid = Number(planYear) >= 2000 && Number(planYear) <= 2200
        && Number(planPercent) >= 0.01 && Number(planPercent) <= 1000;

    const planWhy = savingPlan ? 'Saving is in progress.'
        : !(Number(planYear) >= 2000 && Number(planYear) <= 2200) ? 'Enter a fiscal year from 2000 to 2200.'
        : !planValid ? 'Enter a total percent from 0.01 to 1000.' : null;

    const savePlan = async () => {
        if (!planDialog) return;
        setSavingPlan(true);
        const req = { fiscalYear: Number(planYear), totalPercentOfBasic: Number(planPercent), note: planNote.trim() || null };
        try {
            if (planDialog.id) await PayrollProvisionService.updateFestivalPlan(planDialog.id, req);
            else await PayrollProvisionService.createFestivalPlan(institutionId, req);
            showSuccess('Festival bonus plan saved');
            setPlanDialog(null);
            await load();
        } catch (e) {
            showApiError(e, 'Failed to save the plan');
        } finally {
            setSavingPlan(false);
        }
    };

    const deletePlan = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await PayrollProvisionService.deleteFestivalPlan(deleteTarget.id);
            showSuccess('Plan deleted');
            setDeleteTarget(null);
            await load();
        } catch (e) {
            showApiError(e, 'Failed to delete the plan');
        } finally {
            setDeleting(false);
        }
    };

    const runPreview = async () => {
        setPreviewing(true);
        try {
            setPreview(await PayrollProvisionService.festivalPreview(institutionId, period.year, period.month));
        } catch (e) {
            setPreview(null);
            showApiError(e, 'Failed to preview the festival bonus provision');
        } finally {
            setPreviewing(false);
        }
    };

    const post = async () => {
        setPosting(true);
        try {
            await PayrollProvisionService.festivalPost(institutionId, period);
            showSuccess(`Festival bonus provision for ${periodLabel(period.year, period.month)} posted`);
            setPreview(null);
            await load();
        } catch (e) {
            showApiError(e, 'Failed to post the festival bonus provision');
        } finally {
            setPosting(false);
        }
    };

    const reverse = async () => {
        if (!reverseTarget) return;
        setReversing(true);
        try {
            await PayrollProvisionService.festivalReverse(reverseTarget.id);
            showSuccess('Festival bonus run reversed');
            setReverseTarget(null);
            await load();
        } catch (e) {
            showApiError(e, 'Failed to reverse the run');
        } finally {
            setReversing(false);
        }
    };

    const openDetail = async (id: string) => {
        try {
            setDetail(await PayrollProvisionService.festivalRun(id));
        } catch (e) {
            showApiError(e, 'Failed to load the run');
        }
    };

    const planColumns: Column<IFestivalPlan>[] = [
        { key: 'fy', header: 'Fiscal year', priority: 1, helpKey: 'payroll.festivalProvision.plan.col.fy', render: p => p.fiscalYear },
        { key: 'pct', header: 'Total % of basic', priority: 1, align: 'right', helpKey: 'payroll.festivalProvision.plan.col.pct', render: p => `${p.totalPercentOfBasic}%` },
        { key: 'note', header: 'Note', priority: 3, helpKey: 'payroll.festivalProvision.plan.col.note', render: p => p.note || '—' },
        { key: 'runs', header: 'Runs posted', priority: 2, align: 'right', helpKey: 'payroll.festivalProvision.plan.col.runs', render: p => p.runsPosted },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.festivalProvision.plan.col.actions', role: 'actions', hideInCard: true,
            render: p => canManage ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" data-help="payroll.festivalProvision.plan.action.edit" onClick={() => openPlan(p)}>Edit</Button>
                    <Button size="small" color="error" data-help="payroll.festivalProvision.plan.action.delete" onClick={() => setDeleteTarget(p)}>Delete</Button>
                </Box>
            ) : null,
        },
    ];

    const lineColumns: Column<IFestivalLine>[] = [
        { key: 'seg', header: 'Segment', priority: 1, helpKey: 'payroll.festivalProvision.line.col.seg', render: l => segmentName(l.segmentId) },
        { key: 'est', header: 'Estimate', priority: 2, align: 'right', helpKey: 'payroll.festivalProvision.line.col.est', render: l => money(l.estimate) },
        { key: 'due', header: 'Due to date', priority: 1, align: 'right', helpKey: 'payroll.festivalProvision.line.col.due', render: l => money(l.due) },
        { key: 'mov', header: 'Movement', priority: 1, align: 'right', helpKey: 'payroll.festivalProvision.line.col.mov', render: l => money(l.movement) },
        { key: 'rel', header: 'Release', priority: 2, align: 'right', helpKey: 'payroll.festivalProvision.line.col.rel', render: l => money(l.releaseAmount) },
    ];

    const runColumns: Column<IFestivalRun>[] = [
        { key: 'period', header: 'Period', priority: 1, helpKey: 'payroll.festivalProvision.run.col.period', render: r => periodLabel(r.year, r.month) },
        { key: 'fy', header: 'Fiscal year', priority: 3, helpKey: 'payroll.festivalProvision.run.col.fy', render: r => r.fiscalYear },
        { key: 'me', header: 'Months elapsed', priority: 3, align: 'right', helpKey: 'payroll.festivalProvision.run.col.me', render: r => r.monthsElapsed },
        { key: 'status', header: 'Status', priority: 1, helpKey: 'payroll.festivalProvision.run.col.status', render: r => <RunStatusChip status={r.status} /> },
        { key: 'est', header: 'Estimate', priority: 2, align: 'right', helpKey: 'payroll.festivalProvision.run.col.est', render: r => money(r.estimateTotal) },
        { key: 'due', header: 'Due to date', priority: 2, align: 'right', helpKey: 'payroll.festivalProvision.run.col.due', render: r => money(r.cumulativeDue) },
        { key: 'mov', header: 'Movement', priority: 1, align: 'right', helpKey: 'payroll.festivalProvision.run.col.mov', render: r => money(r.movementTotal) },
        { key: 'rel', header: 'Release', priority: 3, align: 'right', helpKey: 'payroll.festivalProvision.run.col.rel', render: r => money(r.releaseTotal) },
        { key: 'je', header: 'Journal', priority: 3, helpKey: 'payroll.festivalProvision.run.col.je', render: r => <JournalLink id={r.journalEntryId} /> },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.festivalProvision.run.col.actions', role: 'actions', hideInCard: true,
            render: r => (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" data-help="payroll.festivalProvision.run.action.details" onClick={() => openDetail(r.id)}>Details</Button>
                    {canManage && r.status === 'POSTED' && (
                        <Button size="small" color="error" data-help="payroll.festivalProvision.run.action.reverse" onClick={() => setReverseTarget(r)}>Reverse</Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box data-help-page="payroll.festivalProvision.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="h6">Plans</Typography>
                {canManage && (
                    <Button data-help="payroll.festivalProvision.addPlan" variant="contained" startIcon={<AddIcon />} onClick={() => openPlan()} disabled={!institutionId}>Add plan</Button>
                )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The fiscal year of a plan is the calendar year in which the institution's fiscal year starts.
            </Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable data-help-section="payroll.festivalProvision.plansTable" pageKey="festival-plans" columns={planColumns} rows={plans} getRowKey={p => p.id}
                    emptyMessage="No festival bonus plans yet." />
            )}

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Monthly provision</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="payroll.festivalProvision.toolbar">
                <PeriodPicker year={period.year} month={period.month} onChange={p => { setPeriod(p); setPreview(null); }} />
                <Button data-help="payroll.festivalProvision.preview" variant="outlined" onClick={runPreview} disabled={!institutionId || previewing}>Preview</Button>
                {canManage && (
                    <Button data-help="payroll.festivalProvision.post" variant="contained" onClick={post} disabled={!preview || !!preview.blocker || posting}>Post run</Button>
                )}
            </Box>

            {previewing && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
            {preview && !previewing && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Draft for {periodLabel(preview.year, preview.month)}, fiscal year {preview.fiscalYear}
                        {' '}({preview.monthsElapsed} month(s) elapsed)
                    </Typography>
                    <PreviewNotices blocker={preview.blocker} warnings={preview.warnings ?? []} />
                    <SummaryStats items={[
                        { label: 'Estimate (full year)', value: money(preview.estimateTotal) },
                        { label: 'Due to date', value: money(preview.cumulativeDue) },
                        { label: 'Provided before', value: money(preview.providedBefore) },
                        { label: 'Movement', value: money(preview.movementTotal) },
                        { label: 'Release', value: money(preview.releaseTotal) },
                        { label: 'Bonus in payroll', value: money(preview.bonusInPayroll) },
                        { label: 'Provision balance', value: money(preview.balanceBefore) },
                    ]} />
                    <ResponsiveTable data-help-section="payroll.festivalProvision.previewTable" pageKey="festival-preview" columns={lineColumns} rows={preview.lines}
                        getRowKey={l => l.segmentId ?? 'default'} emptyMessage="Nothing to provide for this period." />
                </Box>
            )}

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Runs</Typography>
            <ResponsiveTable data-help-section="payroll.festivalProvision.runsTable" pageKey="festival-runs" columns={runColumns} rows={runs} getRowKey={r => r.id}
                emptyMessage="No festival bonus runs yet." />

            <Dialog open={!!planDialog} onClose={() => setPlanDialog(null)} PaperProps={{ 'data-help-page': 'payroll.festivalProvision.form.page' } as any} maxWidth="xs" fullWidth>
                <DialogTitle>{planDialog?.id ? 'Edit festival bonus plan' : 'Add festival bonus plan'}</DialogTitle>
                <DialogContent data-help-section="payroll.festivalProvision.form.section.details">
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField data-help="payroll.festivalProvision.form.fiscalYear" label="Fiscal year (start year)" type="number" value={planYear} required fullWidth
                            onChange={e => setPlanYear(e.target.value)} inputProps={{ min: 2000, max: 2200 }} />
                        <TextField data-help="payroll.festivalProvision.form.percent" label="Total % of basic" type="number" value={planPercent} required fullWidth
                            onChange={e => setPlanPercent(e.target.value)} inputProps={{ min: 0.01, max: 1000, step: '0.01' }}
                            helperText="Sum of all festival bonuses for the year, as a percent of basic salary." />
                        <TextField data-help="payroll.festivalProvision.form.note" label="Note" value={planNote} fullWidth multiline minRows={2}
                            onChange={e => setPlanNote(e.target.value)} inputProps={{ maxLength: 500 }} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.festivalProvision.form.cancel" onClick={() => setPlanDialog(null)} disabled={savingPlan}>Cancel</Button>
                    <Button data-help="payroll.festivalProvision.form.save" {...helpWhy(planWhy ? { kind: 'status', message: planWhy } : null)} variant="contained" onClick={savePlan} disabled={!planValid || savingPlan}>Save plan</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!deleteTarget} title="Delete plan" confirmLabel="Delete" busy={deleting}
                message={deleteTarget && `Delete the festival bonus plan for fiscal year ${deleteTarget.fiscalYear}?`}
                onConfirm={deletePlan} onClose={() => setDeleteTarget(null)}
            />
            <ConfirmDialog
                open={!!reverseTarget} title="Reverse festival bonus run" confirmLabel="Reverse" busy={reversing}
                message={reverseTarget && `Reverse the festival bonus provision for ${periodLabel(reverseTarget.year, reverseTarget.month)}? Its journal entries are reversed.`}
                onConfirm={reverse} onClose={() => setReverseTarget(null)}
            />

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle>{detail && `Festival bonus run, ${periodLabel(detail.run.year, detail.run.month)}`}</DialogTitle>
                <DialogContent dividers>
                    {detail && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                                <RunStatusChip status={detail.run.status} />
                                <JournalLink id={detail.run.journalEntryId} />
                                {detail.run.releaseJournalEntryId && <JournalLink id={detail.run.releaseJournalEntryId} label="View release JE" />}
                            </Box>
                            <ResponsiveTable data-help-section="payroll.festivalProvision.detailTable" pageKey="festival-run-lines" columns={lineColumns} rows={detail.lines}
                                getRowKey={l => l.segmentId ?? 'default'} emptyMessage="No lines." />
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button data-help="payroll.festivalProvision.detailClose" onClick={() => setDetail(null)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default FestivalProvisionTab;
